import { useEffect } from 'react';

import { isIosNative } from '@/renderer/utils/isIosNative';

/**
 * Keeps a CSS custom property in sync with the *visible* viewport height
 * (`window.visualViewport.height`), so a layout can size itself to the area
 * that isn't covered by the on-screen keyboard.
 *
 * Why this exists: `100dvh` measures the full viewport, not the space left
 * above a soft keyboard. On the Capacitor **Android** shell the WebView does
 * not resize when the keyboard opens, so a `100dvh` chat layout pushes its
 * composer down behind the keyboard where it can't be seen or used (#1477).
 * `visualViewport` reports the real visible height on every platform, so
 * driving the height from it fixes Android without regressing web.
 *
 * The property is set on `<html>` so it can feed the whole height chain
 * (including the `.less`-scoped `html/body/#root` rules). It is removed on
 * unmount so it only affects the page that opts in.
 *
 * It also maintains `--support-kb-closed` (1 while the keyboard is down, 0 while
 * up) so the composer can drop its home-indicator safe-area padding when the
 * keyboard already covers the home indicator (#1579), and cancels the stray
 * document scroll WKWebView triggers on input focus (#1579).
 *
 * ## iOS native path (#1755)
 *
 * `--support-vh` is driven from `visualViewport.height` on **every** platform,
 * iOS included. `#1602` had instead computed the iOS height from the
 * `@capacitor/keyboard` `keyboardWillShow`/`keyboardDidShow` events
 * (`innerHeight - keyboardHeight`, corrected once on didShow). That single-shot
 * correction was timing-fragile: the iOS shell (`Keyboard.resize: 'native'`)
 * resizes the WebView ~0.45s *after* keyboardWillShow, so a `didShow` read of
 * `visualViewport` could latch the pre-resize (full) height and leave the
 * composer stranded behind the keyboard until the next keyboard cycle, with no
 * self-correction (the support-chat regression, #1755). Driving from
 * `visualViewport` continuously self-corrects: the shell's resize fires a
 * `visualViewport` `resize` event, and the listener re-reads the settled height.
 *
 * Two iOS-only nuances remain:
 *  - `--support-kb-closed` cannot use the `innerHeight - visualViewport` gap on
 *    iOS: the shell shrinks the WebView so `innerHeight` shrinks *with* the
 *    visible viewport and the gap stays ~0 even with the keyboard up. So on iOS
 *    the flag is driven from the `@capacitor/keyboard` show/hide events instead.
 *  - The shell's resize is async, so for the ~0.45s before it lands
 *    `visualViewport` still reports the full height. `keyboardWillShow` sets a
 *    proactive target (`innerHeight - keyboardHeight`) so the composer lifts
 *    immediately instead of flashing behind the keyboard; it's guarded against a
 *    double-subtract (keyboard already up). While the keyboard is up we also
 *    *clamp* `sync()` so it can only shrink `--support-vh` toward the true
 *    visible height, never grow it back to full — otherwise a stale full-height
 *    read during that window bounces the composer down and back up (a visible
 *    double-bounce). `visualViewport` still self-corrects downward once the
 *    resize lands; the clamp is released on `keyboardWillHide`.
 *
 * SSR-safe: all access to `window` happens inside the effect.
 */
export function useVisualViewportHeight(varName = '--support-vh'): void {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const root = document.documentElement;
    const vv = window.visualViewport;
    const iosNative = isIosNative();

    const setKbClosed = (closed: boolean) => {
      // `--support-kb-closed` is 1 when the keyboard is down and 0 when it's up,
      // so chrome can multiply the home-indicator inset by it and drop that
      // padding once the keyboard covers the home indicator — otherwise the
      // composer floats a full safe-area gap above the keyboard (#1579).
      root.style.setProperty('--support-kb-closed', closed ? '1' : '0');
    };

    // iOS WKWebView, on input focus, scrolls its whole scroll view up to
    // "reveal" the field above the keyboard — but the layout already resizes to
    // the area above the keyboard, so that native scroll pushes the composer
    // clean off the top of the screen (#1579). The page itself is
    // `overflow: hidden` (SupportChat.less), so the document should never be
    // scrolled; force it back to 0. Harmless where the scroll is already 0. The
    // inner message list has its own scroll container and is unaffected.
    const cancelStrayScroll = () => {
      if (window.scrollX !== 0 || window.scrollY !== 0) {
        window.scrollTo(0, 0);
      }
    };

    // While the keyboard is up on iOS native, `sync()` may only *shrink*
    // `--support-vh` toward the true visible height, never grow it back toward
    // full. This is the ceiling it clamps to — the proactive/last-settled lifted
    // target from `keyboardWillShow`. `null` means no clamp (keyboard down, or
    // Android/web, which must keep driving straight from `visualViewport`).
    //
    // Why: the iOS shell (`Keyboard.resize: 'native'`) resizes the WebView
    // ~0.45s *after* `keyboardWillShow`. For that window `visualViewport.height`
    // still reports the full (pre-resize) height, so any `sync()` that fires
    // then (a focus `scroll`/`cancelStrayScroll`, a `resize`, the rAF/300ms
    // `syncSettled` tail) would overwrite the proactive lifted target back to
    // full — the composer drops, then lifts again once the resize lands: a
    // visible double-bounce (#1755 follow-up). Clamping to the ceiling holds the
    // lifted position through that window while still letting `visualViewport`
    // self-correct downward to the true visible height once the resize lands.
    let kbCeiling: number | null = null;

    const sync = () => {
      let height = Math.round(vv?.height ?? window.innerHeight);
      // Drive the height from the *visible* viewport on every platform. On iOS
      // the shell resizes the WebView (so this shrinks once the resize lands);
      // on Android/web the WebView stays full-height and this is what excludes
      // the keyboard. Either way, re-reading on every resize/scroll self-corrects
      // any transient (mid-animation, post-rotation, delayed native resize).
      if (iosNative && kbCeiling !== null) {
        // Never grow past the lifted target while the keyboard is up — a stale
        // full-height read during the shell's async resize would bounce us down.
        height = Math.min(height, kbCeiling);
      }
      root.style.setProperty(varName, `${height}px`);
      // `--support-kb-closed` from the visible-vs-layout gap works on Android/web
      // (the WebView doesn't resize, so the gap is the keyboard). It does NOT work
      // on iOS native — the shell resizes the WebView so `innerHeight` shrinks
      // with it and the gap stays ~0 — so there the flag is driven from the
      // keyboard show/hide events below.
      if (!iosNative) {
        setKbClosed(window.innerHeight - height <= 120);
      }
      cancelStrayScroll();
    };

    // ---- iOS native: keyboard events drive --support-kb-closed + a proactive
    // height target while the shell's async WebView resize catches up (#1755) ----
    let cleanupKb: (() => void) | undefined;
    if (iosNative) {
      void import('@capacitor/keyboard')
        .then(({ Keyboard }) => {
          const willShow = Keyboard.addListener('keyboardWillShow', (info) => {
            setKbClosed(false);
            // The shell resizes the WebView ~0.45s after this fires, so
            // `visualViewport` still reports the full height for now. Set the
            // target immediately so the composer lifts in step with the keyboard
            // instead of flashing behind it. `innerHeight - keyboardHeight` is the
            // area above the keyboard; guard against a double-subtract when the
            // keyboard is already up (innerHeight already reduced) by only taking
            // a sane (> half-screen) target. `sync()`/visualViewport corrects it.
            const target = window.innerHeight - info.keyboardHeight;
            if (target > window.screen.height / 2) {
              const rounded = Math.round(target);
              // Arm the clamp so any sync() firing before the shell's async
              // resize lands can't grow --support-vh back to the full height.
              kbCeiling = rounded;
              root.style.setProperty(varName, `${rounded}px`);
            }
          });
          // Re-read once the keyboard has settled, catching the shell's resize.
          const didShow = Keyboard.addListener('keyboardDidShow', () => sync());
          const willHide = Keyboard.addListener('keyboardWillHide', () => {
            setKbClosed(true);
            // Release the clamp so sync() can restore the full height (the shell
            // grows the WebView back); the visualViewport `resize` fires too, but
            // call sync() so it's immediate.
            kbCeiling = null;
            sync();
          });

          cleanupKb = () => {
            void willShow.then((h) => h.remove());
            void didShow.then((h) => h.remove());
            void willHide.then((h) => h.remove());
          };
        })
        .catch(() => {
          // Plugin unavailable — the visualViewport listeners still drive
          // --support-vh; only the --support-kb-closed flag stays at its default.
        });
    }

    // A single read can hold a stale height in two cases, so re-read across the
    // next frame and shortly after (the trailing `resize`/`scroll` events fire
    // too, but this guarantees a correction even if they don't):
    //  - `orientationchange`: many WebViews still report the pre-rotation
    //    (portrait) height at the instant the event fires (#1516).
    //  - Initial mount: on a push deep-link the chat mounts and its composer
    //    auto-focuses at the same time, so the keyboard rises together with the
    //    first paint and that first frame is mis-sized (the input is pushed up
    //    with a large gap above the keyboard) until the layout settles (#1516,
    //    #1566). The 300ms tail covers the keyboard-open animation.
    let rafId = 0;
    let timeoutId = 0;
    const syncSettled = () => {
      sync();
      window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(sync);
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(sync, 300);
    };

    syncSettled();

    // `resize` covers keyboard open/close and browser-chrome changes; `scroll`
    // keeps us correct if the visual viewport is panned while the keyboard is up.
    vv?.addEventListener('resize', sync);
    vv?.addEventListener('scroll', sync);
    window.addEventListener('resize', sync);
    window.addEventListener('orientationchange', syncSettled);
    // Also catch (and cancel) the stray document scroll the native keyboard
    // triggers on input focus — see `cancelStrayScroll`.
    window.addEventListener('scroll', sync, { passive: true });

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      vv?.removeEventListener('resize', sync);
      vv?.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
      window.removeEventListener('orientationchange', syncSettled);
      window.removeEventListener('scroll', sync);
      cleanupKb?.();
      root.style.removeProperty(varName);
      root.style.removeProperty('--support-kb-closed');
    };
  }, [varName]);
}
